import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Play, Pause, Zap, Radio, ListChecks, History } from 'lucide-react';
import { CommanServices } from '@/services/commanServices';
import { toast } from 'sonner';

const fmtElapsed = (ms) => {
  if (ms < 0) ms = 0;
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h > 0 ? h + 'h ' : ''}${m}m ${sec}s`;
};

export const ExerciseControlPanel = ({
  incident,
  onRefresh,
  canControlRuntime = true,
  canManageInjectors = true,
}) => {
  const [state, setState] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    if (!incident?.id) return;
    try {
      const res = await CommanServices.getExerciseLog(incident.id);
      if (res?.status) setState(res.data);
    } catch (e) { /* noop */ }
  }, [incident?.id]);

  useEffect(() => {
    load();
    const poll = setInterval(load, 8000);
    timerRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(poll); clearInterval(timerRef.current); };
  }, [load]);

  if (String(incident?.status || '').toLowerCase() !== 'active' || !incident?.is_exercise) return null;

  const paused = state?.paused;
  const activatedAt = state?.activated_at ? new Date(state.activated_at).getTime() : null;
  const elapsed = activatedAt && !paused ? now - activatedAt : (activatedAt ? now - activatedAt : 0);

  const handlePause = async () => {
    setBusy(true);
    try {
      const res = await CommanServices.toggleExercisePause(incident.id);
      if (res?.status) { toast.success(res.message || 'Updated'); window.dispatchEvent(new Event('exercisePauseChanged')); await load(); }
    } finally { setBusy(false); }
  };

  const handleFire = async (itemId) => {
    setBusy(true);
    try {
      const res = await CommanServices.fireInjectNow(incident.id, itemId);
      if (res?.status) { toast.success('Inject fired'); await load(); onRefresh && onRefresh(true); }
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-slate-900 text-white p-5 rounded-xl shadow-lg border border-slate-700" data-testid="exercise-control-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-emerald-400 animate-pulse" />
          <h3 className="text-[11px] font-black uppercase tracking-widest text-emerald-300">Exercise Control · Live</h3>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-wide text-slate-400">Elapsed</div>
            <div className="text-sm font-mono font-bold text-white" data-testid="exercise-elapsed">{fmtElapsed(elapsed)}{paused ? ' (paused)' : ''}</div>
          </div>
          {canControlRuntime && <button
            onClick={handlePause}
            disabled={busy}
            data-testid="exercise-pause-btn"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${paused ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-600 hover:bg-amber-500'} disabled:opacity-50`}
          >
            {paused ? <><Play className="w-3.5 h-3.5" /> Resume</> : <><Pause className="w-3.5 h-3.5" /> Pause</>}
          </button>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Pending injects */}
        <div className="bg-slate-800/60 rounded-lg p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <ListChecks className="w-3.5 h-3.5 text-sky-400" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-300">Pending Injects ({state?.pending?.length || 0})</span>
          </div>
          <div className="space-y-1.5 max-h-44 overflow-y-auto">
            {(state?.pending || []).length === 0 && <p className="text-xs text-slate-500">All injects have fired.</p>}
            {(state?.pending || []).map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 bg-slate-900/60 rounded px-2 py-1.5" data-testid={`pending-inject-${p.id}`}>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-white truncate">{p.title}</p>
                  <p className="text-[10px] text-slate-400">T+{p.fireOffsetMinutes || 0}m · {p.group?.replace('_', ' ')}</p>
                </div>
                {canManageInjectors && <button
                  onClick={() => handleFire(p.id)}
                  disabled={busy}
                  data-testid={`fire-now-${p.id}`}
                  className="flex items-center gap-1 px-2 py-1 rounded bg-rose-600 hover:bg-rose-500 text-[10px] font-bold disabled:opacity-50 shrink-0"
                >
                  <Zap className="w-3 h-3" /> Fire now
                </button>}
              </div>
            ))}
          </div>
        </div>

        {/* Escalation log */}
        <div className="bg-slate-800/60 rounded-lg p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <History className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-300">Activity Log</span>
          </div>
          <div className="space-y-1 max-h-44 overflow-y-auto">
            {(state?.log || []).length === 0 && <p className="text-xs text-slate-500">No activity yet.</p>}
            {(state?.log || []).map((l, i) => (
              <div key={i} className="text-[11px] text-slate-300 border-l-2 border-amber-500/40 pl-2 py-0.5">
                <span className="text-slate-500">{l.at ? new Date(l.at).toLocaleTimeString() : ''}</span> · {l.text}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseControlPanel;
